import { MouseEventHandler, ReactNode } from 'react'
import clsx from 'clsx'

import { BackButton, PaytrieTopIcon } from '@/components/widget-components'
import { TransactionType, useBuySellStore } from '@/reducers/transactionReducer'

const Step = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => {
  return (
    <div
      data-testid="container-info-step"
      className={clsx(
        'grid-start flex gap-5 flex-col text-base font-normal leading-tight cursor-pointer mb-5',
        className
      )}
    >
      {children}
    </div>
  )
}

const StepHeader = ({ children }: { children: ReactNode }) => {
  return (
    <div
      data-testid="text-info-step-header"
      className="font-normal uppercase leading-none tracking-wide"
    >
      {children}
    </div>
  )
}

const StepContent = ({ children }: { children: ReactNode }) => {
  return (
    <div
      data-testid="text-info-step-content"
      className="text-base font-normal leading-tight"
    >
      {children}
    </div>
  )
}

export const InfoPage = ({
  onHide,
  showBack,
}: {
  onHide: MouseEventHandler
  showBack: boolean
}) => {
  const transactionType = useBuySellStore.use.transactionType()
  return (
    <div
      data-testid="container-info-page"
      className={
        showBack
          ? 'top-0 bottom-0 left-0 right-0 absolute dark:bg-stone-900 bg-cloud overflow-y-scroll p-5 z-10'
          : 'mt-4 relative'
      }
    >
      {showBack && (
        <BackButton
          aria-label="Go back"
          onClick={onHide}
          data-testid="btn-info-back"
        >
          &#x27f5;
        </BackButton>
      )}

      <PaytrieTopIcon
        className="absolute top-5 right-5 hidden md:block"
        data-testid="icon-info-top"
      />

      {transactionType === TransactionType.BUY && (
        <div data-testid="container-info-buy">
          <div
            data-testid="text-info-buy-title"
            className="text-4xl font-normal leading-10 mb-12 mt-4"
          >
            Buying Stablecoins
          </div>
          <div
            data-testid="container-info-steps"
            className="display flex gap-5"
          >
            <div
              data-testid="container-info-steps-left"
              className="flex-1 w-1/2"
            >
              <Step className="col-start-1">
                <StepHeader>Step 1</StepHeader>
                <StepContent>
                  Select network and token and enter the amount of stablecoins
                  you want to buy.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 2</StepHeader>
                <StepContent>
                  Copy and paste your ETH or stablecoin wallet address into the
                  provided input box.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 3</StepHeader>
                <StepContent>
                  Determine if you need some ETH in your wallet to help transfer
                  the stablecoins in the future. Highly recommended for new
                  users or new wallets.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 4</StepHeader>
                <div data-testid="text-info-step-content">
                  Submit your transaction to PayTrie. This will lock in your
                  price.
                </div>
              </Step>
            </div>
            <div
              data-testid="container-info-steps-right"
              className="flex-1 w-1/2"
            >
              <Step className="lg:col-start-2 lg:row-start-1">
                <div
                  data-testid="text-info-step-header"
                  className="text-base font-normal uppercase leading-none tracking-wide"
                >
                  Step 5
                </div>
                <StepContent>
                  Check your email inbox for a Request Money Transfer email from
                  Interac. Follow the instructions in the email to fund the
                  transaction with your bank account.
                </StepContent>
              </Step>

              <Step className="lg:col-start-2 lg:row-start-2">
                <StepHeader>Step 6</StepHeader>
                <StepContent>
                  Wait for PayTrie to accept the funds (up to 60 mins, you will
                  get an email notification when PayTrie accepts the funds)
                </StepContent>
              </Step>

              <Step className="lg:col-start-2 lg:row-start-3">
                <StepHeader>Step 7</StepHeader>
                <StepContent>
                  Recieve an email confirmation when the transaction is complete
                  and the stable coins are in your wallet
                </StepContent>
              </Step>
            </div>
          </div>
        </div>
      )}

      {transactionType === TransactionType.SELL && (
        <div data-testid="container-info-sell">
          <div
            data-testid="text-info-sell-title"
            className="text-4xl font-normal leading-10 mb-12 mt-4"
          >
            Selling Stablecoins
          </div>
          <div
            data-testid="container-info-steps"
            className="display flex gap-5"
          >
            <div
              data-testid="container-info-steps-left"
              className="flex-1 w-1/2"
            >
              <Step className="col-start-1">
                <StepHeader>Step 1</StepHeader>
                <StepContent>
                  Click the sell button in the top right corner of the widget to
                  flip between buy and sell.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 2</StepHeader>
                <StepContent>
                  Select network and token and enter the amount of stablecoins
                  you want to sell.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 3</StepHeader>
                <StepContent>
                  Copy and paste your ETH or stablecoin wallet address that you
                  will be sending from into the provided input box.
                </StepContent>
              </Step>
              <Step className="col-start-1">
                <StepHeader>Step 4</StepHeader>
                <div data-testid="text-info-step-content">
                  Submit your transaction to PayTrie. This will lock in your
                  price.
                </div>
              </Step>
            </div>
            <div
              data-testid="container-info-steps-right"
              className="flex-1 w-1/2"
            >
              <Step className="lg:col-start-2 lg:row-start-1">
                <div
                  data-testid="text-info-step-header"
                  className="text-base font-normal uppercase leading-none tracking-wide"
                >
                  Step 5
                </div>
                <StepContent>
                  Using the wallet currently hodling the stablecoins, send the
                  specific amount to the address provided by Paytrie.
                </StepContent>
              </Step>

              <Step className="lg:col-start-2 lg:row-start-2">
                <StepHeader>Step 6</StepHeader>
                <StepContent>
                  Wait for PayTrie to accept the funds (up to 60 mins, you will
                  get an email notification when PayTrie accepts the funds)
                </StepContent>
              </Step>

              <Step className="lg:col-start-2 lg:row-start-3">
                <StepHeader>Step 7</StepHeader>
                <StepContent>
                  Have additional questions? Check out our FAQ or email us at
                  support@paytrie.com!
                </StepContent>
              </Step>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
